import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Redirect } from 'expo-router';
import { api } from '../../src/api/client';
import { useAdminPermissions } from '../../src/hooks/useAdminPermissions';
import { AdminToolbar } from '../../src/components/AdminList';
import { AdminAnalyticsChart, SERIES_DEFS, type Range, type Granularity, type TimeseriesData } from '../../src/components/AdminAnalyticsChart';
import ContentContainer from '../../src/components/ContentContainer';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

const MOBILE_BREAKPOINT = 768;
const CENTS_PER_DOLLAR = 100;

interface RevenueSums { totalCharged: number; totalFees: number; ownerRevenue: number; count: number }
interface PeriodBreakdown { all: RevenueSums; nonFleet: RevenueSums; fleet: RevenueSums }
interface AnalyticsData {
  revenue: {
    today: PeriodBreakdown; thisWeek: PeriodBreakdown; thisMonth: PeriodBreakdown;
    lastMonth: PeriodBreakdown; yearToDate: PeriodBreakdown; allTime: PeriodBreakdown;
  };
  recurring: {
    platformMrr: number; totalSubscriptionMrr: number;
    activeSubscriptionCount: number; avgSubscriptionRevenue: number;
  };
  activations: {
    activeCount: number; billedCount: number; waivedCount: number;
    graceCount: number; cancelledCount: number;
    activationMrr: number;
    activatedThisMonth: number; cancelledThisMonth: number;
    avgTenureDays: number; churnPctThisMonth: number;
  };
  ops: {
    activeOwnersToday: number; activeOwnersThisMonth: number; activeLocationsThisMonth: number;
    avgTxValueThisMonth: number; newOwnersThisMonth: number; totalLiveOwners: number;
    refundsCountThisMonth: number; refundsAmountThisMonth: number;
  };
}

interface TopOwner {
  id: string; businessName: string; email: string;
  activationStatus: string | null;
  activationWaiverType: string | null;
  activatedDays: number | null;
  deactivatedDays: number | null;
  allTime: number; allTimeFees: number; allTimeCount: number;
  thisMonth: number; thisMonthFees: number; thisMonthCount: number;
}
interface TopLocation {
  id: string; name: string; businessName: string | null;
  allTime: number; allTimeFees: number; allTimeCount: number;
  thisMonth: number; thisMonthFees: number; thisMonthCount: number;
}

const fmt = (cents: number) => `$${(cents / CENTS_PER_DOLLAR).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const SORT_OPTIONS = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Platform Fees', value: 'platformFees' },
];

const OWNER_SORT_OPTIONS = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Platform Fees', value: 'platformFees' },
  { label: 'Tenure', value: 'activatedDays' },
];

const ACTIVATION_PILLS: Record<string, { label: string; bg: string; fg: string }> = {
  active:        { label: 'Active',    bg: '#05966915', fg: '#059669' },
  grace_period:  { label: 'Grace',     bg: '#d9770615', fg: '#d97706' },
  inactive:      { label: 'Cancelled', bg: '#dc262615', fg: '#dc2626' },
};

const PERIOD_ROWS: Array<{ key: keyof AnalyticsData['revenue']; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'thisWeek', label: 'This Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'yearToDate', label: 'Year to Date' },
  { key: 'allTime', label: 'All Time' },
];

export default function AdminAnalytics() {
  const { has, loaded } = useAdminPermissions();
  if (loaded && !has('platform_analytics')) return <Redirect href="/admin/users" />;
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;
  const styles = useStyles(colors, isMobile);

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Top owners
  const [owners, setOwners] = useState<TopOwner[]>([]);
  const [ownersTotal, setOwnersTotal] = useState(0);
  const [ownersPage, setOwnersPage] = useState(1);
  const [ownersSearch, setOwnersSearch] = useState('');
  const [ownersSort, setOwnersSort] = useState('');
  const [ownersLoading, setOwnersLoading] = useState(true);

  // Top locations
  const [locs, setLocs] = useState<TopLocation[]>([]);
  const [locsTotal, setLocsTotal] = useState(0);
  const [locsPage, setLocsPage] = useState(1);
  const [locsSearch, setLocsSearch] = useState('');
  const [locsSort, setLocsSort] = useState('');
  const [locsLoading, setLocsLoading] = useState(true);

  // Chart
  const [tsData, setTsData] = useState<TimeseriesData | null>(null);
  const [tsLoading, setTsLoading] = useState(true);
  const [range, setRange] = useState<Range>('30d');
  const [gran, setGran] = useState<Granularity | null>(null);
  const [selectedSeries, setSelectedSeries] = useState<Set<string>>(
    () => new Set(SERIES_DEFS.filter(s => s.defaultOn).map(s => s.key))
  );

  const fetchTimeseries = useCallback(async (r: Range, g: Granularity | null, signal?: AbortSignal) => {
    setTsLoading(true);
    try {
      const params = new URLSearchParams({ range: r });
      if (g) params.set('granularity', g);
      const d = await api<TimeseriesData>(`/api/admin/analytics/timeseries?${params}`);
      if (signal?.aborted) return;
      setTsData(d);
    } catch (err) { if (!signal?.aborted) console.error(err); }
    finally { if (!signal?.aborted) setTsLoading(false); }
  }, []);

  const toggleSeries = (k: string) => {
    setSelectedSeries(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const fetchOverview = useCallback(async (signal?: AbortSignal) => {
    try {
      const d = await api<AnalyticsData>('/api/admin/analytics');
      if (signal?.aborted) return;
      setData(d);
    } catch (err) { if (!signal?.aborted) console.error(err); }
    finally { if (!signal?.aborted) { setLoading(false); setRefreshing(false); } }
  }, []);

  const fetchOwners = useCallback(async (p = 1, s = '', sort = '', signal?: AbortSignal) => {
    setOwnersLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      if (s) params.set('search', s);
      if (sort) params.set('sort', sort);
      const d = await api<{ owners: TopOwner[]; total: number }>(`/api/admin/analytics/top-owners?${params}`);
      if (signal?.aborted) return;
      setOwners(d.owners);
      setOwnersTotal(d.total);
      setOwnersPage(p);
    } catch (err) { if (!signal?.aborted) console.error(err); }
    finally { if (!signal?.aborted) setOwnersLoading(false); }
  }, []);

  const fetchLocs = useCallback(async (p = 1, s = '', sort = '', signal?: AbortSignal) => {
    setLocsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      if (s) params.set('search', s);
      if (sort) params.set('sort', sort);
      const d = await api<{ locations: TopLocation[]; total: number }>(`/api/admin/analytics/top-locations?${params}`);
      if (signal?.aborted) return;
      setLocs(d.locations);
      setLocsTotal(d.total);
      setLocsPage(p);
    } catch (err) { if (!signal?.aborted) console.error(err); }
    finally { if (!signal?.aborted) setLocsLoading(false); }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchOverview(ac.signal);
    fetchOwners(1, '', '', ac.signal);
    fetchLocs(1, '', '', ac.signal);
    fetchTimeseries(range, gran, ac.signal);
    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchTimeseries(range, gran, ac.signal);
    return () => ac.abort();
  }, [range, gran, fetchTimeseries]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchOverview();
    fetchOwners(ownersPage, ownersSearch, ownersSort);
    fetchLocs(locsPage, locsSearch, locsSort);
    fetchTimeseries(range, gran);
  };

  const ownersCsv = `/api/admin/analytics/top-owners?${new URLSearchParams({ ...(ownersSearch && { search: ownersSearch }), ...(ownersSort && { sort: ownersSort }) })}`;
  const locsCsv = `/api/admin/analytics/top-locations?${new URLSearchParams({ ...(locsSearch && { search: locsSearch }), ...(locsSort && { sort: locsSort }) })}`;

  if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  if (!data) return <View style={styles.center}><Text style={styles.empty}>Failed to load analytics</Text></View>;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
    >
      <ContentContainer role="admin">

      {/* Header banner */}
      <View style={styles.banner}>
        <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
        <Text style={styles.bannerText}>All metrics include Stripe-settled payments only. Comp washes, gift credits, and voucher-only redemptions are excluded.</Text>
      </View>

      {/* Timeseries chart */}
      <AdminAnalyticsChart
        data={tsData}
        loading={tsLoading}
        range={range}
        onRangeChange={setRange}
        granularity={gran}
        onGranularityChange={setGran}
        selectedKeys={selectedSeries as Set<any>}
        onToggleSeries={toggleSeries as any}
      />

      {/* Revenue by period */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Revenue by Period</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.colPeriod, styles.h]}>Period</Text>
          <Text style={[styles.colFees, styles.h]}>Platform Fees</Text>
          <Text style={[styles.colCount, styles.h]}>Washes</Text>
          <Text style={[styles.colGross, styles.h]}>Gross Sales</Text>
          {!isMobile && <Text style={[styles.colFleet, styles.h]}>Fleet Share</Text>}
        </View>
        {PERIOD_ROWS.map(({ key, label }, i) => {
          const p = data.revenue[key];
          const isAllTime = key === 'allTime';
          return (
            <View key={key} style={[styles.row, i === PERIOD_ROWS.length - 1 && styles.lastRow, isAllTime && styles.allTimeRow]}>
              <Text style={[styles.colPeriod, styles.cell, isAllTime && styles.cellBold]}>{label}</Text>
              <Text style={[styles.colFees, styles.cell, styles.cellNum, isAllTime && styles.cellBoldPrimary]}>{fmt(p.all.totalFees)}</Text>
              <Text style={[styles.colCount, styles.cell, styles.cellNum]}>{p.all.count.toLocaleString()}</Text>
              <Text style={[styles.colGross, styles.cell, styles.cellNum]}>{fmt(p.all.totalCharged)}</Text>
              {!isMobile && (
                <Text style={[styles.colFleet, styles.cell, styles.cellNumSub]}>
                  {p.all.count > 0 ? `${Math.round((p.fleet.count / p.all.count) * 100)}%` : '—'}
                </Text>
              )}
            </View>
          );
        })}
      </View>

      {/* Recurring */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Recurring (Subscriptions)</Text>
        <LineItem label="Platform MRR" value={fmt(data.recurring.platformMrr)} icon="trending-up" color="#7c3aed" styles={styles} />
        <LineItem label="Total Subscriber MRR" value={fmt(data.recurring.totalSubscriptionMrr)} sub="paid to owners" styles={styles} />
        <LineItem label="Active Subscriptions" value={data.recurring.activeSubscriptionCount.toLocaleString()} styles={styles} />
        <LineItem label="Avg Revenue / Subscription" value={fmt(data.recurring.avgSubscriptionRevenue)} sub="monthly" last styles={styles} />
      </View>

      {/* Activations — $100/mo platform fee */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Activations ($100/mo)</Text>
        <LineItem
          label="Activation MRR"
          value={fmt(data.activations.activationMrr)}
          sub={`${data.activations.billedCount} billed · ${data.activations.waivedCount} waived`}
          icon="ribbon" color="#10b981" styles={styles}
        />
        <LineItem label="Active Activations" value={data.activations.activeCount.toLocaleString()} styles={styles} />
        <LineItem label="In Grace Period" value={data.activations.graceCount.toLocaleString()} sub="failed payment" styles={styles} />
        <LineItem label="Cancelled" value={data.activations.cancelledCount.toLocaleString()} styles={styles} />
        <LineItem label="Activated This Month" value={data.activations.activatedThisMonth.toLocaleString()} styles={styles} />
        <LineItem
          label="Cancelled This Month"
          value={data.activations.cancelledThisMonth.toLocaleString()}
          sub={`${data.activations.churnPctThisMonth.toFixed(1)}% churn`}
          icon="trending-down" color="#dc2626" styles={styles}
        />
        <LineItem
          label="Avg Active Tenure"
          value={`${data.activations.avgTenureDays.toLocaleString()} days`}
          last styles={styles}
        />
      </View>

      {/* Operations */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Operations (This Month)</Text>
        <LineItem label="Active Owners" value={`${data.ops.activeOwnersThisMonth} / ${data.ops.totalLiveOwners}`} sub={`${data.ops.activeOwnersToday} today`} icon="people" color="#059669" styles={styles} />
        <LineItem label="Active Locations" value={data.ops.activeLocationsThisMonth.toLocaleString()} styles={styles} />
        <LineItem label="Avg Transaction Value" value={fmt(data.ops.avgTxValueThisMonth)} styles={styles} />
        <LineItem label="New Owners" value={data.ops.newOwnersThisMonth.toLocaleString()} styles={styles} />
        <LineItem label="Refunds Issued" value={fmt(data.ops.refundsAmountThisMonth)} sub={`${data.ops.refundsCountThisMonth} refunds`} icon="return-down-back" color="#dc2626" last styles={styles} />
      </View>

      {/* Top Owners */}
      <Text style={styles.sectionTitle}>Top Owners by Revenue</Text>
      <View style={styles.card}>
        <AdminToolbar
          search={ownersSearch}
          onSearchChange={setOwnersSearch}
          onSearch={() => fetchOwners(1, ownersSearch, ownersSort)}
          total={ownersTotal}
          page={ownersPage}
          limit={10}
          onPageChange={(p) => fetchOwners(p, ownersSearch, ownersSort)}
          csvEndpoint={ownersCsv}
          csvFilename="top-owners.csv"
          label="owner"
          filterOptions={OWNER_SORT_OPTIONS}
          activeFilter={ownersSort}
          onFilterChange={(v) => { setOwnersSort(v); fetchOwners(1, ownersSearch, v); }}
        />
        {ownersLoading ? (
          <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
        ) : owners.length === 0 ? (
          <Text style={styles.empty}>No owners found</Text>
        ) : (
          owners.map((o, i) => {
            const pill = o.activationStatus ? ACTIVATION_PILLS[o.activationStatus] : null;
            const waived = o.activationStatus === 'active' && o.activationWaiverType;
            const tenureText =
              o.activationStatus === 'inactive' && o.deactivatedDays !== null
                ? `cancelled ${o.deactivatedDays}d ago`
                : o.activatedDays !== null
                  ? `activated ${o.activatedDays}d ago`
                  : 'not activated';
            return (
              <View key={o.id} style={styles.listRow}>
                <Text style={styles.rank}>#{(ownersPage - 1) * 10 + i + 1}</Text>
                <View style={styles.listBody}>
                  <View style={styles.listNameRow}>
                    <Text style={styles.listName} numberOfLines={1}>{o.businessName}</Text>
                    {pill && (
                      <View style={[styles.pill, { backgroundColor: pill.bg }]}>
                        <Text style={[styles.pillText, { color: pill.fg }]}>{pill.label}</Text>
                      </View>
                    )}
                    {waived && (
                      <View style={[styles.pill, { backgroundColor: '#7c3aed15' }]}>
                        <Text style={[styles.pillText, { color: '#7c3aed' }]}>Waived</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.listSub} numberOfLines={1}>{o.email}</Text>
                  <Text style={styles.listMeta} numberOfLines={1}>
                    {tenureText} · {o.allTimeCount.toLocaleString()} washes all-time · {o.thisMonthCount.toLocaleString()} this month
                  </Text>
                </View>
                <View style={styles.listRight}>
                  <Text style={styles.listAmount}>{fmt(o.allTime)}</Text>
                  <Text style={styles.listSubRight}>{fmt(o.thisMonth)} this mo</Text>
                  <Text style={styles.listSubRight}>{fmt(o.allTimeFees)} fees</Text>
                </View>
              </View>
            );
          })
        )}
      </View>

      {/* Top Locations */}
      <Text style={styles.sectionTitle}>Top Locations by Revenue</Text>
      <View style={styles.card}>
        <AdminToolbar
          search={locsSearch}
          onSearchChange={setLocsSearch}
          onSearch={() => fetchLocs(1, locsSearch, locsSort)}
          total={locsTotal}
          page={locsPage}
          limit={10}
          onPageChange={(p) => fetchLocs(p, locsSearch, locsSort)}
          csvEndpoint={locsCsv}
          csvFilename="top-locations.csv"
          label="location"
          filterOptions={SORT_OPTIONS}
          activeFilter={locsSort}
          onFilterChange={(v) => { setLocsSort(v); fetchLocs(1, locsSearch, v); }}
        />
        {locsLoading ? (
          <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
        ) : locs.length === 0 ? (
          <Text style={styles.empty}>No locations found</Text>
        ) : (
          locs.map((l, i) => (
            <View key={l.id} style={styles.listRow}>
              <Text style={styles.rank}>#{(locsPage - 1) * 10 + i + 1}</Text>
              <View style={styles.listBody}>
                <Text style={styles.listName} numberOfLines={1}>{l.name}</Text>
                {l.businessName && <Text style={styles.listSub} numberOfLines={1}>{l.businessName}</Text>}
                <Text style={styles.listMeta} numberOfLines={1}>
                  {l.allTimeCount.toLocaleString()} washes all-time · {l.thisMonthCount.toLocaleString()} this month
                </Text>
              </View>
              <View style={styles.listRight}>
                <Text style={styles.listAmount}>{fmt(l.allTime)}</Text>
                <Text style={styles.listSubRight}>{fmt(l.thisMonth)} this mo</Text>
                <Text style={styles.listSubRight}>{fmt(l.allTimeFees)} fees</Text>
              </View>
            </View>
          ))
        )}
      </View>

      </ContentContainer>
    </ScrollView>
  );
}

function LineItem({ label, value, sub, icon, color, last, styles }: {
  label: string; value: string; sub?: string; icon?: any; color?: string; last?: boolean;
  styles: ReturnType<typeof createStyles>;
}) {
  return (
    <View style={[styles.lineItem, last && styles.lineItemLast]}>
      <View style={styles.lineLeft}>
        {icon && color && (
          <View style={[styles.iconDot, { backgroundColor: color + '15' }]}>
            <Ionicons name={icon} size={14} color={color} />
          </View>
        )}
        <Text style={styles.lineLabel}>{label}</Text>
      </View>
      <View style={styles.lineRight}>
        <Text style={styles.lineValue}>{value}</Text>
        {sub && <Text style={styles.lineSub}>{sub}</Text>}
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: isMobile ? spacing.md : spacing.lg },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm, marginTop: spacing.md },
  empty: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.md, fontSize: fontSize.sm },

  banner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: c.surface, borderRadius: borderRadius.md,
    borderLeftWidth: 3, borderLeftColor: c.primary,
    marginBottom: spacing.md,
  },
  bannerText: { fontSize: fontSize.xs, color: c.textSecondary, flex: 1 },

  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg,
    padding: spacing.md, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  cardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },

  // Revenue table
  tableHeader: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  h: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, textTransform: 'uppercase' },
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  lastRow: { borderBottomWidth: 0 },
  allTimeRow: { backgroundColor: c.background, marginHorizontal: -spacing.md, paddingHorizontal: spacing.md, borderBottomWidth: 0 },
  cell: { fontSize: fontSize.sm, color: c.text },
  cellNum: { fontVariant: ['tabular-nums'] },
  cellNumSub: { fontVariant: ['tabular-nums'], color: c.textSecondary, fontSize: fontSize.xs },
  cellBold: { fontWeight: fontWeight.bold },
  cellBoldPrimary: { fontWeight: fontWeight.bold, color: c.primary },
  colPeriod: { flex: isMobile ? 1.2 : 1.4 },
  colFees: { flex: 1.2, textAlign: 'right', paddingRight: spacing.sm },
  colCount: { flex: 0.8, textAlign: 'right', paddingRight: spacing.sm },
  colGross: { flex: 1.2, textAlign: 'right', paddingRight: spacing.sm },
  colFleet: { flex: 0.8, textAlign: 'right' },

  // Line items (recurring + ops)
  lineItem: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  lineItemLast: { borderBottomWidth: 0 },
  lineLeft: { flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 },
  iconDot: { width: 24, height: 24, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  lineLabel: { fontSize: fontSize.sm, color: c.text },
  lineRight: { alignItems: 'flex-end' },
  lineValue: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, fontVariant: ['tabular-nums'] },
  lineSub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },

  // Top owners / locations list rows
  listRow: {
    flexDirection: 'row', alignItems: 'flex-start',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm,
  },
  rank: { fontSize: fontSize.xs, fontWeight: fontWeight.bold, color: c.textLight, width: 28, paddingTop: 2 },
  listBody: { flex: 1, minWidth: 0 },
  listNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  listName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  pill: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 },
  pillText: { fontSize: 10, fontWeight: fontWeight.semibold },
  listSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  listMeta: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  listRight: { alignItems: 'flex-end' },
  listAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  listSubRight: { fontSize: fontSize.xs, color: c.textLight, fontVariant: ['tabular-nums'], marginTop: 1 },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
